import { useParams } from 'react-router-dom';

import { cn, RoundButton } from '@yakit/ui-components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Alert,
  AlertDescription,
  AlertTitle,
  IconLucide
} from '@yakit/ui-components';

import { useFetchButtons } from '@VDL/hooks/useFetchButtons';
import type { IVDLButton } from '@VDL/api/types/IVDLButton';

export type OnClickButtonObject = (buttonObject: IVDLButton) => void;

const VDLToolbarButton = ({
  buttonObject,
  childButtons,
  activeButtonObjectCode,
  onClickButtonObject
}: {
  buttonObject: IVDLButton;
  childButtons: IVDLButton[];
  activeButtonObjectCode: string;
  onClickButtonObject: OnClickButtonObject;
}) => {
  if (childButtons.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <RoundButton
            className={cn('group cursor-pointer', {
              'text-primary/80 hover:text-primary': childButtons.some(
                (childObject) => childObject.code === activeButtonObjectCode
              )
            })}
            iconName={buttonObject.icon ?? 'Airplay'}
          >
            {buttonObject.name}
            <IconLucide
              name="ChevronDown"
              className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180"
            />
          </RoundButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="rounded-xl"
          align="start"
          sideOffset={8}
        >
          {childButtons.map((childObject) => (
            <DropdownMenuItem
              key={childObject.code}
              onClick={() => onClickButtonObject(childObject)}
              className={cn('cursor-pointer', {
                'text-primary/80 hover:text-primary': activeButtonObjectCode === childObject.code
              })}
            >
              <IconLucide name={childObject.icon} />
              {childObject.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <RoundButton
      iconName={buttonObject.icon ?? 'Airplay'}
      className={cn('cursor-pointer', {
        'text-primary/80 hover:text-primary': activeButtonObjectCode === buttonObject.code
      })}
      onClick={() => onClickButtonObject(buttonObject)}
    >
      {buttonObject.name}
    </RoundButton>
  );
};

export const VDLBannerToolbarButtons = ({
  activeButtonObjectCode,
  onClickButtonObject
}: {
  activeButtonObjectCode: string;
  onClickButtonObject: OnClickButtonObject;
}) => {
  const { level0Code } = useParams();
  const { buttons, error, loading } = useFetchButtons(level0Code);

  if (loading) {
    return (
      <div className="flex gap-2">
        {[...Array(5)].map((_, index) => (
          <Skeleton key={index} className="h-[2.375rem] w-24 rounded-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="text-destructive">
        <AlertTitle>Не удалось загрузить кнопки</AlertTitle>
        <AlertDescription>Произошла ошибка при запросе кнопок</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {buttons.map((buttonObject) => {
        const childButtons = buttons.filter(
          (buttonChild) => buttonChild.parent_code === buttonObject.code
        );

        if (buttonObject.parent_code) return;

        return (
          <VDLToolbarButton
            key={buttonObject.code}
            buttonObject={buttonObject}
            childButtons={childButtons}
            activeButtonObjectCode={activeButtonObjectCode}
            onClickButtonObject={onClickButtonObject}
          />
        );
      })}
    </div>
  );
};
